import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Collapse, Grid, MenuItem, Stack } from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { makeZodI18nMap } from 'zod-i18n-map'
import { useHasEnablement } from '@portal/pkg-authorization'
import { HookFormTextField, HookFormSelect, HookFormCheckbox } from '@portal/pkg-hook-form'
import { InfoLabel, SideDrawer } from '@portal/pkg-ui'
import {
  AddUserSchema,
  AddUserFormValues,
  addUserDefaults,
  LicenseType,
  IntegrationRoleOption,
  NotificationFrequencyOption,
} from '../model/addUserSchema'
import { useCreateUser } from '../mutations'
import { UserQueries } from '../queries'
import {
  isLicenseLimitExceeded,
  isOrganizationBusy,
  isUserAlreadyExists,
  isUserNotCreated,
} from '../problems'

interface AddUserFormProps {
  onClose: () => void
  orgId?: number
}

export function AddUserForm({ onClose, orgId }: AddUserFormProps) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const hasAdminEnablement = useHasEnablement('Admin', 'ADMIN', { global: true })
  const { data: userAdminIntegrationStatus } = useSuspenseQuery(UserQueries.orgSettings(orgId))
  const { mutate: createUserMutation } = useCreateUser(orgId)

  const form = useForm<AddUserFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(AddUserSchema, {
      errorMap: makeZodI18nMap({ handlePath: false }),
    }),
    defaultValues: addUserDefaults,
  })

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = form

  const emailNotifications = useWatch({ control, name: 'emailNotifications' })

  const onSubmit = (data: AddUserFormValues) => {
    createUserMutation(data, {
      onSuccess: () => {
        enqueueSnackbar(t('users.table.userAdded'), { variant: 'success' })
        onClose()
      },
      onError: (error: unknown) => {
        if (isUserAlreadyExists(error)) {
          enqueueSnackbar(t('users.form.emailAlreadyInUse'), { variant: 'error' })
        } else if (isUserNotCreated(error)) {
          // The email belongs to a user this organization cannot see. The message must not say so.
          enqueueSnackbar(t('users.form.userNotCreated'), { variant: 'error' })
        } else if (isLicenseLimitExceeded(error)) {
          enqueueSnackbar(t('users.form.licenseLimitExceeded'), { variant: 'error' })
        } else if (isOrganizationBusy(error)) {
          enqueueSnackbar(t('users.form.organizationBusy'), { variant: 'error' })
        } else {
          enqueueSnackbar(t('users.form.saveFailed'), { variant: 'error' })
        }
      },
    })
  }

  return (
    <SideDrawer
      title={t('users.drawer.addTitle')}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={1}>
          <Button color="secondary" onClick={onClose}>
            {t('users.drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isValid}
            onClick={() => void handleSubmit(onSubmit)()}
          >
            {t('users.drawer.save')}
          </Button>
        </Stack>
      }
    >
      <FormProvider {...form}>
        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <HookFormTextField<AddUserFormValues>
                name="givenName"
                label={t('users.form.firstName')}
                variant="outlined"
                required
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <HookFormTextField<AddUserFormValues>
                name="familyName"
                label={t('users.form.lastName')}
                variant="outlined"
                required
                fullWidth
                size="small"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <HookFormTextField<AddUserFormValues>
                name="email"
                label={t('users.form.email')}
                variant="outlined"
                type="email"
                required
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <HookFormSelect<AddUserFormValues>
                name="licenseType"
                label={
                  <InfoLabel
                    label={t('users.form.licenseType')}
                    tooltip={t('users.form.licenseTypeHelp')}
                  />
                }
                required
                fullWidth
                size="small"
              >
                <MenuItem value={LicenseType.LITE}>{t('users.form.licenseTypes.lite')}</MenuItem>
                <MenuItem value={LicenseType.POWER}>{t('users.form.licenseTypes.power')}</MenuItem>
                {hasAdminEnablement && (
                  <MenuItem value={LicenseType.ADMIN}>
                    {t('users.form.licenseTypes.admin')}
                  </MenuItem>
                )}
              </HookFormSelect>
            </Grid>

            {hasAdminEnablement && userAdminIntegrationStatus.integrationsEnabled && (
              <Grid size={{ xs: 6 }}>
                <HookFormSelect<AddUserFormValues>
                  name="integrationsRole"
                  label={
                    <InfoLabel
                      label={t('users.form.integrationsRole')}
                      tooltip={t('users.form.integrationsRoleHelp')}
                    />
                  }
                  fullWidth
                  size="small"
                >
                  <MenuItem value={IntegrationRoleOption.NOT_SELECTED}>
                    {t('users.form.integrationsRoles.notSelected')}
                  </MenuItem>
                  <MenuItem value={IntegrationRoleOption.ANALYST}>
                    {t('users.form.integrationsRoles.analyst')}
                  </MenuItem>
                  <MenuItem value={IntegrationRoleOption.OPERATOR}>
                    {t('users.form.integrationsRoles.operator')}
                  </MenuItem>
                  <MenuItem value={IntegrationRoleOption.ADMIN}>
                    {t('users.form.integrationsRoles.admin')}
                  </MenuItem>
                </HookFormSelect>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Stack>
                <HookFormCheckbox<AddUserFormValues>
                  name="projectParticipant"
                  label={t('users.form.projectParticipant')}
                  helperText={t('users.form.projectParticipantHelp')}
                />
                <HookFormCheckbox<AddUserFormValues>
                  name="emailNotifications"
                  label={t('users.form.emailNotifications')}
                  helperText={t('users.form.emailNotificationsHelp')}
                />
              </Stack>

              <Collapse in={emailNotifications}>
                <Box sx={{ pl: 4, pt: 2, width: '50%' }}>
                  <HookFormSelect<AddUserFormValues>
                    name="notificationFrequency"
                    label={t('users.form.notificationFrequency')}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value={NotificationFrequencyOption.DAILY}>
                      {t('users.form.frequencies.daily')}
                    </MenuItem>
                    <MenuItem value={NotificationFrequencyOption.WEEKLY}>
                      {t('users.form.frequencies.weekly')}
                    </MenuItem>
                    <MenuItem value={NotificationFrequencyOption.MONTHLY}>
                      {t('users.form.frequencies.monthly')}
                    </MenuItem>
                  </HookFormSelect>
                </Box>
              </Collapse>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </SideDrawer>
  )
}
